<template>
  <PageContainer>
    <template v-slot:body>
      <Container
        :class="$style.containerPhase"
      >
        <div :class="$style.innerContainer">
          <HomeWelcomeSection :class="$style.welcomeSection" />
          <!-- Primary Widgets -->
          <GridAuto
            :class="$style.widgetGridPhase"
            data-test-id="HomeMainPanel__cardWidget"
            minWidth="18rem"
          >
            <UserLockoutWidget v-if="hasUsers && orgInsights.userLockouts.visible" />
            <ExpiredPasswordWidget
              v-if="hasUsers && orgInsights.expiredPasswords.visible"
            />
            <UpcomingPasswordExpirationWidget
              v-if="hasUsers && orgInsights.upcomingPasswordExpiration.visible"
            />
            <NewUsersWidget v-if="hasUsers && orgInsights.newUsers.visible" />
            <ScheduledUserSuspensionsWidget v-if="showScheduledUserSuspension" />
            <ScheduledUserActivationsWidget v-if="showScheduledUserActivation" />
            <ReportsReadyForDownload v-if="showReportsReadyForDownload" />
            <UserGroupSuggestion v-if="showUserGroupSuggestions" />
          </GridAuto>
          <div :class="$style.widgetGridPhase">
            <MDMCertificateExpirationsWidget
              v-if="showMdmCertificate"
            />
          </div>
          <GridAuto
            :class="$style.widgetGridPhase"
            data-test-id="HomeMainPanel__notificationWidget"
          >
            <DeviceNotificationsWidget v-if="orgInsights.deviceNotifications.visible" />
            <UserNotificationsWidget v-if="orgInsights.userNotifications.visible" />
            <RecentOSReleasesWidget v-if="hasSystems && orgInsights.newOSReleases.visible" />
            <GroupMembershipWidget v-if="showGroupMembership" />
          </GridAuto>
          <HomeSupportSection :class="$style.supportSection" />
        </div>
      </Container>
    </template>
  </PageContainer>
</template>

<script>
import { Container, GridAuto, PageContainer } from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import DeviceNotificationsWidget from 'client/HomePageV2/widgets/DeviceNotificationsWidget.vue';
import ExpiredPasswordWidget from 'client/HomePageV2/widgets/ExpiredPasswordWidget.vue';
import GroupMembershipWidget from 'client/HomePageV2/widgets/GroupMembershipWidget.vue';
import HomeSupportSection from 'client/HomePageV2/components/HomeSupportSection.vue';
import HomeWelcomeSection from 'client/HomePageV2/components/HomeWelcomeSection.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import MDMCertificateExpirationsWidget from 'client/HomePageV2/widgets/MDMCertificateExpirationsWidget';
import NewUsersWidget from 'client/HomePageV2/widgets/NewUsersWidget';
import RecentOSReleasesWidget from 'client/HomePageV2/widgets/RecentOSReleasesWidget.vue';
import ReportsReadyForDownload from 'client/HomePageV2/widgets/ReportsReadyForDownload.vue';
import ScheduledUserActivationsWidget from 'client/HomePageV2/widgets/ScheduledUserActivationsWidget.vue';
import ScheduledUserSuspensionsWidget from 'client/HomePageV2/widgets/ScheduledUserSuspensionsWidget.vue';
import UpcomingPasswordExpirationWidget from 'client/HomePageV2/widgets/UpcomingPasswordExpirationWidget.vue';
import UserGroupSuggestion from 'client/HomePageV2/widgets/UserGroupSuggestion.vue';
import UserLockoutWidget from 'client/HomePageV2/widgets/UserLockoutWidget.vue';
import UserNotificationsWidget from 'client/HomePageV2/widgets/UserNotificationsWidget.vue';

const systemModule = StoreModuleMap[ItemTypeConsts.system];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const settingsModel = StoreModelMap[ItemTypeConsts.settings];
const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];
const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];
const administratorModel = 'AdministratorModel';

export default {
  name: 'HomeMainPanel',

  components: {
    Container,
    DeviceNotificationsWidget,
    ExpiredPasswordWidget,
    GridAuto,
    GroupMembershipWidget,
    HomeSupportSection,
    HomeWelcomeSection,
    MDMCertificateExpirationsWidget,
    NewUsersWidget,
    PageContainer,
    RecentOSReleasesWidget,
    ReportsReadyForDownload,
    ScheduledUserActivationsWidget,
    ScheduledUserSuspensionsWidget,
    UserNotificationsWidget,
    UpcomingPasswordExpirationWidget,
    UserGroupSuggestion,
    UserLockoutWidget,
  },

  props: {
  },

  LocalStorageService,
  data() {
    return {
      hasSystems: undefined,
      hasUsers: undefined,
      isLoadingWidgetDisplay: true,
      settingsRoute: ItemTypeRoutes[ItemTypeConsts.settingsHome],
    };
  },

  computed: {
    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),

    ...mapState('FeatureFlagsModule', {
      homePageWidgetsPhase2: state => state.betaFeatures.homePageWidgetsPhase2,
    }),

    ...mapState(settingsModel, {
      settings: 'item',
    }),

    ...mapState(settingsModule, ['orgId']),

    ...mapState(administratorModel, [
      'mdmVisibility',
    ]),

    orgInsights() {
      return this.displaySettings.display;
    },

    showGroupMembership() {
      return this.hasUsers
      && this.orgInsights.manualGroupMembership.visible;
    },

    showMdmCertificate() {
      return this.orgInsights.mdmCertificateExpirations.visible
          && this.mdmVisibility;
    },

    showReportsReadyForDownload() {
      return (this.hasUsers || this.hasSystems)
      && this.orgInsights.reportsReadyForDownload.visible;
    },

    showScheduledUserActivation() {
      return this.hasUsers
            && this.orgInsights?.scheduledUserActivations.visible;
    },

    showScheduledUserSuspension() {
      return this.hasUsers
            && this.orgInsights?.scheduledUserSuspensions.visible;
    },

    showUserGroupSuggestions() {
      return this.hasUsers
      && this.orgInsights.userGroupSuggestions.visible;
    },
  },

  created() {
    this.resetUsers();
    this.fetchDisplayPreferencesData();
    this.getHasSystems();
    this.getHasUsers();
  },

  destroyed() {
    this.resetMdm(true);
  },

  methods: {
    ...mapActions(userDisplayPreferencesModule, [
      'fetchDisplayPreferencesData',
    ]),

    ...mapActions(administratorModel, ['resetMdm']),

    ...mapActions(systemUserCollection, {
      fetchUsers: 'fetchCollection',
      resetUsers: 'resetCollection',
    }),

    ...mapActions(systemModule, {
      fetchSystemsTotalCount: 'fetchNoFilterTotalCount',
    }),

    getHasSystems() {
      return this.fetchSystemsTotalCount()
        .then((totalCount) => {
          this.hasSystems = !!totalCount;
        })
        .catch(() => {
          this.hasSystems = null;
        });
    },

    getHasUsers() {
      return this.fetchUsers()
        .then((ids) => {
          this.hasUsers = !!ids.length;
        })
        .catch(() => {
          this.hasUsers = null;
        });
    },
  },
};
</script>
<style module scoped>
.containerPhase {
  background-color: var(--jc-color-background);
}

.innerContainer {
  max-width: 80rem;
}

.widgetGridPhase {
  padding-bottom: var(--jc-spacer);
}

.supportSection {
  padding-top: var(--jc-spacer);
}

.welcomeSection {
  margin: var(--jc-spacer) 0 var(--jc-spacer-medium);
  padding: var(--jc-spacer-medium) var(--jc-spacer-large);
}
</style>
